// @flow
import config from '../../config';
import {
  makeAPICall,
  APIError,
  getCurrentOrgIdByContext
} from '../generalCreators';
import { updateCurrentUser } from './currentUserCreators';
import { getLoggedInUser } from '../authentication/authCreators';
import type { ThunkAction, User } from '../../types';

export function getUsers(
  organisationId?: number,
  isAllocatedTo?: boolean = true,
  excludeArchived?: boolean = false
): ThunkAction {
  return function(dispatch, getState) {
    const endpoint = '/users';
    const options = {
      method: 'GET',
      headers: { token: getState().authentication.token },
      params: {
        productId: config.productId,
        organisationId,
        isAllocatedTo,
        excludeArchived
      }
    };
    return makeAPICall(endpoint, options).then(
      response => dispatch({ type: 'UPDATE_USERS', users: response.data }),
      error => dispatch(APIError(error, 'Error getting Users'))
    );
  };
}

export function getFilteredUsers(
  text: string,
  excludeArchived: ?boolean = true
): ThunkAction {
  return function(dispatch, getState) {
    const endpoint = '/users/filter';
    const options = {
      method: 'GET',
      headers: { token: getState().authentication.token },
      params: {
        productId: config.productId,
        text,
        excludeArchived
      }
    };

    return makeAPICall(endpoint, options).then(
      response => {
        dispatch({ type: 'UPDATE_USERS', users: response.data });
      },
      error => dispatch(APIError(error, 'Error getting filtered users'))
    );
  };
}

export function addPermissionsToUser(user: User, settings: Object): User {
  const permissions = [];
  // add user permission automatically
  const userPermission = settings.availablePermissions.find(
    p => p.permissionName === 'User'
  );

  permissions.push(userPermission);

  const adminPermission = settings.availablePermissions.find(
    p => p.permissionName === 'Admin'
  );

  if (user.isAdmin && adminPermission) {
    permissions.push(adminPermission);
  }

  // add permissions to user
  user.permissions = permissions;
  return user;
}

export function addUser(
  user: User,
  settings: Object,
  context: string
): ThunkAction {
  return function(dispatch, getState) {
    user = addPermissionsToUser(user, settings);
    const organisationId = getCurrentOrgIdByContext(context, getState);
    const endpoint = '/users';
    const options = {
      method: 'POST',
      headers: { token: getState().authentication.token },
      params: {
        productId: config.productId,
        organisationId
      },
      data: user
    };

    return makeAPICall(endpoint, options).then(
      response => {
        const newUserId = response.data.id;
        dispatch(getUsers(organisationId));
        dispatch(updateCurrentUser(newUserId, organisationId));
        return response;
      },
      error => dispatch(APIError(error, 'Error creating User'))
    );
  };
}

export function updateUser(
  user: User | Object,
  settings: Object,
  context: string,
  archiveUser: boolean = false
): ThunkAction {
  return function(dispatch, getState) {
    if (archiveUser === true) {
      user = {
        id: user.id,
        dateUpdated: user.dateUpdated,
        isArchived: user.isArchived
      };
    } else {
      user = addPermissionsToUser(user, settings);
    }
    const organisationId = getCurrentOrgIdByContext(context, getState);
    const endpoint = `/user/${user.id}`;
    const options = {
      method: 'PUT',
      headers: { token: getState().authentication.token },
      params: {
        productId: config.productId,
        organisationId
      },
      data: user
    };
    return makeAPICall(endpoint, options).then(
      response => {
        dispatch(getUsers(organisationId));
        dispatch(updateCurrentUser(user.id, organisationId));
        return response;
      },
      error => dispatch(APIError(error, 'Error updating User'))
    );
  };
}

export function getUserSettings(organisationId: number): ThunkAction {
  return function(dispatch, getState) {
    const endpoint = '/user/settings';
    const options = {
      method: 'GET',
      headers: { token: getState().authentication.token },
      params: {
        productId: config.productId,
        organisationId
      }
    };
    return makeAPICall(endpoint, options).then(
      response => {
        const settings = response.data;
        dispatch({ type: 'UPDATE_CURRENT_ORG_USER_SETTINGS', settings });
        return response;
      },
      error => dispatch(APIError(error, 'Error getting User Settings'))
    );
  };
}

// $FlowFixMe
export const setHasSeenDashboard = (): ThunkAction => {
  return function(dispatch, getState) {
    const endpoint = `/user/current`;
    const options = {
      method: 'PUT',
      headers: { token: getState().authentication.token },
      params: {
        productId: config.productId
      },
      data: {
        hasSeenDashboard: true,
        dateUpdated: new Date().toISOString()
      }
    };
    return makeAPICall(endpoint, options).then(
      response => {
        dispatch(getLoggedInUser());
        return response;
      },
      error => dispatch(APIError(error, 'Error updating User'))
    );
  };
};

export function updateUserInternalAdmin(
  user: User | Object,
  archiveUser: boolean = false
): ThunkAction {
  return function(dispatch, getState) {
    if (archiveUser === true) {
      user = {
        id: user.id,
        dateUpdated: user.dateUpdated,
        isArchived: user.isArchived
      };
    }

    const endpoint = `/user/basic/${user.id}`;
    const options = {
      method: 'PUT',
      headers: { token: getState().authentication.token },
      params: {
        productId: config.productId
      },
      data: user
    };
    return makeAPICall(endpoint, options).then(
      response => {
        return response;
      },
      error => dispatch(APIError(error, 'Error updating User'))
    );
  };
}



// WEBPACK FOOTER //
// ./src/actions/users/basicCreators.js